import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FilterState } from '../filters/config/types';
import { workdStorage } from '../storage/workdStorage';

/**
 * Cached count entry with expiration time from backend
 */
export interface CachedCount {
	count: number;
	expiresAt: number;
}

/**
 * Cache structure for counts by list ID
 */
export interface CachedCounts {
	[listId: string]: CachedCount;
}

/**
 * List preferences (view mode, page size, etc.)
 */
export interface ListPreferences {
	viewMode?: 'table' | 'grid' | 'kanban' | 'map' | 'week' | 'month' | 'day';
	pageSize?: number;
	columns?: Array<string>;
}

/**
 * Failed list entry for retry tracking
 */
export interface FailedListEntry {
	retryCount: number;
	lastAttempt: number;
}

/**
 * Sort field for list navigation
 */
export interface ListSortField {
	// Optional to mirror the shared SortField: the backend emits bare-alias
	// sorts (computed columns) when the table is absent.
	objectTable?: string;
	fieldName: string;
	direction: string;
}

/**
 * Navigation context stored when navigating from list to detail
 */
export interface ListNavigationContext {
	listId?: string;
	listName?: string;
	sortFields: Array<ListSortField>;
	filters: Record<string, unknown>;
	filterSummary?: string;
	sourceUrl: string;
	timestamp: number;
}

/**
 * Navigation state stored per list (filter, search, sort, page)
 * This is session-scoped state for list navigation (browser tab lifetime)
 */
export interface ListSessionNavigationState {
	filterState: FilterState;
	search: string;
	sortId: string;
	sortFields: Array<ListSortField>;
	page: number;
	timestamp: number;
	/**
	 * Marks `filterState` as the COMPLETE set of user-chosen filters, written
	 * by a surface that knows the whole state (the client map's "List" handoff).
	 * Restorers may replace the current filters outright instead of merging —
	 * merging resurrects list-config defaults the user explicitly removed on
	 * the other surface (PD-13205). Absent on snapshots from older sessions,
	 * which keep the historical merge semantics.
	 */
	completeFilterState?: boolean;
}

/**
 * List store state
 */
export interface ListStoreState {
	// Counts cache: { "clients": { "list-1": { count: 10, expiresAt: 123 } } }
	counts: Record<string, CachedCounts>;

	// Failed lists tracking: { "clients": { "list-1": { retryCount: 1, lastAttempt: 123 } } }
	failedLists: Record<string, Record<string, FailedListEntry>>;

	// Preferences: { "clients": { viewMode: "table", pageSize: 25 } }
	prefs: Record<string, ListPreferences>;

	// Filter collapsed state: { "clients": true, "items": false }
	// A key is present only once the user has explicitly toggled that list's
	// filter panel. An absent key means "no choice yet" — the caller's default
	// applies. Storing the resolved default here would make it indistinguishable
	// from a real user preference.
	filterCollapsed: Record<string, boolean>;

	// Builder configurations
	builderConfigs: Array<unknown>;

	// Active lists
	activeLists: Array<string>;

	// Navigation context: { "client": { sortFields: [...], filters: {...}, ... } }
	navigationContexts: Record<string, ListNavigationContext>;

	// Session navigation state: { "clients:list-1": { filterState: {...}, ... } }
	// Used to restore list state when navigating back
	navigationStates: Record<string, ListSessionNavigationState>;
}

/**
 * List store actions
 */
export interface ListStoreActions {
	// Counts
	setCounts: (contextKey: string, counts: CachedCounts) => void;
	setCount: (contextKey: string, listId: string, count: CachedCount) => void;
	getCounts: (contextKey: string) => CachedCounts | undefined;
	clearCounts: (contextKey?: string) => void;

	// Failed lists
	markListFailed: (contextKey: string, listId: string) => void;
	clearFailedList: (contextKey: string, listId: string) => void;
	clearContextFailedLists: (contextKey: string) => void;
	getFailedLists: (contextKey: string) => Record<string, FailedListEntry>;

	// Preferences
	setPrefs: (objectType: string, prefs: ListPreferences) => void;
	getPrefs: (objectType: string) => ListPreferences | undefined;
	clearPrefs: (objectType?: string) => void;

	// Filter collapsed
	setFilterCollapsed: (listKey: string, collapsed: boolean) => void;
	getFilterCollapsed: (listKey: string) => boolean | undefined;

	// Builder configs
	setBuilderConfigs: (configs: Array<unknown>) => void;
	setActiveLists: (lists: Array<string>) => void;

	// Navigation contexts
	setNavigationContext: (
		listKey: string,
		context: ListNavigationContext,
	) => void;
	getNavigationContext: (listKey: string) => ListNavigationContext | null;
	clearNavigationContext: (listKey: string) => void;
	clearAllNavigationContexts: () => void;

	// Session navigation states
	setNavigationState: (
		key: string,
		state: Partial<ListSessionNavigationState>,
	) => void;
	getNavigationState: (key: string) => ListSessionNavigationState | null;
	clearNavigationState: (key: string) => void;
	clearAllNavigationStates: () => void;

	// Clear all
	clearAll: () => void;
}

// Navigation context TTL (30 minutes)
const NAVIGATION_CONTEXT_TTL = 30 * 60 * 1000;
// Navigation state TTL (30 minutes)
const NAVIGATION_STATE_TTL = 30 * 60 * 1000;

const initialState: ListStoreState = {
	counts: {},
	failedLists: {},
	prefs: {},
	filterCollapsed: {},
	builderConfigs: [],
	activeLists: [],
	navigationContexts: {},
	navigationStates: {},
};

// Max retries for failed lists
const MAX_RETRIES = 5;
// Failed entries expire after 5 minutes
const FAILED_ENTRY_MAX_AGE = 5 * 60 * 1000;

/**
 * Persisted-state schema version for this store.
 *
 * v1 - `filterCollapsed` entries used to be written on every mount, so merely
 *      visiting a list persisted that list's resolved default as though the
 *      user had chosen it. The Client Shop panel now defaults to collapsed, and
 *      any user who had visited it before carries a stale `client_shop: false`
 *      that would keep it expanded forever. The migration drops that one key so
 *      the new default applies; every other key is left alone.
 */
export const LIST_STORE_PERSIST_VERSION = 1;

// The only list whose filter default changed, so the only stale entry we drop.
const EAGERLY_PERSISTED_LIST_KEY = 'client_shop';

/**
 * Migrate a persisted `workd:list:state` payload forward. Written as a named
 * export so the behaviour is unit-testable without driving the persist
 * middleware.
 */
export function migrateListState(
	persistedState: unknown,
	version: number,
): unknown {
	if (version >= LIST_STORE_PERSIST_VERSION) {
		return persistedState;
	}

	if (persistedState == null || typeof persistedState !== 'object') {
		return persistedState;
	}

	const state = persistedState as Partial<ListStoreState>;
	const filterCollapsed = state.filterCollapsed;

	if (filterCollapsed == null || typeof filterCollapsed !== 'object') {
		return persistedState;
	}

	if (!(EAGERLY_PERSISTED_LIST_KEY in filterCollapsed)) {
		return persistedState;
	}

	const migrated = { ...filterCollapsed };
	delete migrated[EAGERLY_PERSISTED_LIST_KEY];

	return { ...state, filterCollapsed: migrated };
}

/**
 * Zustand list store with persistence
 */
export const useListStore = create<ListStoreState & ListStoreActions>()(
	persist(
		(set, get) => ({
			...initialState,

			// Counts
			// Replaces the context's counts. Every caller passes the full set
			// (read, merged, pruned), and merging here put back what the caller
			// had pruned: a count whose recount failed (PD-13954) and entries past
			// the cache's one-hour hard limit.
			setCounts: (contextKey, counts) => {
				set((state) => ({
					counts: {
						...state.counts,
						[contextKey]: { ...counts },
					},
				}));
			},

			setCount: (contextKey, listId, count) => {
				set((state) => ({
					counts: {
						...state.counts,
						[contextKey]: {
							...state.counts[contextKey],
							[listId]: count,
						},
					},
				}));
			},

			getCounts: (contextKey) => get().counts[contextKey],

			clearCounts: (contextKey) => {
				if (contextKey != null) {
					set((state) => {
						const newCounts = { ...state.counts };
						delete newCounts[contextKey];

						return { counts: newCounts };
					});
				} else {
					set({ counts: {} });
				}
			},

			// Failed lists
			markListFailed: (contextKey, listId) => {
				set((state) => {
					const existing = state.failedLists[contextKey]?.[listId];
					const now = Date.now();

					return {
						failedLists: {
							...state.failedLists,
							[contextKey]: {
								...state.failedLists[contextKey],
								[listId]: {
									retryCount: existing != null ? existing.retryCount + 1 : 1,
									lastAttempt: now,
								},
							},
						},
					};
				});
			},

			clearFailedList: (contextKey, listId) => {
				set((state) => {
					if (state.failedLists[contextKey]?.[listId] == null) {
						return state;
					}

					const newContextFailed = { ...state.failedLists[contextKey] };
					delete newContextFailed[listId];

					return {
						failedLists: {
							...state.failedLists,
							[contextKey]: newContextFailed,
						},
					};
				});
			},

			clearContextFailedLists: (contextKey) => {
				set((state) => {
					const newFailed = { ...state.failedLists };
					delete newFailed[contextKey];
					return { failedLists: newFailed };
				});
			},

			getFailedLists: (contextKey) => {
				const failed = get().failedLists[contextKey] ?? {};
				const now = Date.now();
				const result: Record<string, FailedListEntry> = {};

				// Filter out expired entries and those that have exceeded max retries
				for (const [listId, entry] of Object.entries(failed)) {
					if (
						now - entry.lastAttempt <= FAILED_ENTRY_MAX_AGE &&
						entry.retryCount < MAX_RETRIES
					) {
						result[listId] = entry;
					}
				}

				return result;
			},

			// Preferences
			setPrefs: (objectType, prefs) => {
				set((state) => ({
					prefs: {
						...state.prefs,
						[objectType]: {
							...state.prefs[objectType],
							...prefs,
						},
					},
				}));
			},

			getPrefs: (objectType) => get().prefs[objectType],

			clearPrefs: (objectType) => {
				if (objectType != null) {
					set((state) => {
						const newPrefs = { ...state.prefs };
						delete newPrefs[objectType];

						return { prefs: newPrefs };
					});
				} else {
					set({ prefs: {} });
				}
			},

			// Filter collapsed
			setFilterCollapsed: (listKey, collapsed) => {
				set((state) => ({
					filterCollapsed: {
						...state.filterCollapsed,
						[listKey]: collapsed,
					},
				}));
			},

			getFilterCollapsed: (listKey) => get().filterCollapsed[listKey],

			// Builder configs
			setBuilderConfigs: (builderConfigs) => {
				set({ builderConfigs });
			},

			setActiveLists: (activeLists) => {
				set({ activeLists });
			},

			// Navigation contexts
			setNavigationContext: (listKey, context) => {
				set((state) => ({
					navigationContexts: {
						...state.navigationContexts,
						[listKey]: context,
					},
				}));
			},

			getNavigationContext: (listKey) => {
				const context = get().navigationContexts[listKey];

				if (context == null) {
					return null;
				}

				// Check if context is expired
				if (Date.now() - context.timestamp > NAVIGATION_CONTEXT_TTL) {
					return null;
				}

				return context;
			},

			clearNavigationContext: (listKey) => {
				set((state) => {
					const newContexts = { ...state.navigationContexts };
					delete newContexts[listKey];

					return { navigationContexts: newContexts };
				});
			},

			clearAllNavigationContexts: () => {
				set({ navigationContexts: {} });
			},

			// Session navigation states
			setNavigationState: (key, partialState) => {
				set((state) => {
					const existing = state.navigationStates[key];
					const newState: ListSessionNavigationState = {
						filterState:
							partialState.filterState ?? existing?.filterState ?? {},
						search: partialState.search ?? existing?.search ?? '',
						sortId: partialState.sortId ?? existing?.sortId ?? '',
						sortFields: partialState.sortFields ?? existing?.sortFields ?? [],
						page: partialState.page ?? existing?.page ?? 1,
						timestamp: Date.now(),
					};

					return {
						navigationStates: {
							...state.navigationStates,
							[key]: newState,
						},
					};
				});
			},

			getNavigationState: (key) => {
				const state = get().navigationStates[key];

				if (state == null) {
					return null;
				}

				// Check if expired
				if (Date.now() - state.timestamp > NAVIGATION_STATE_TTL) {
					return null;
				}

				return state;
			},

			clearNavigationState: (key) => {
				set((state) => {
					const newStates = { ...state.navigationStates };
					delete newStates[key];

					return { navigationStates: newStates };
				});
			},

			clearAllNavigationStates: () => {
				set({ navigationStates: {} });
			},

			// Clear all
			clearAll: () => {
				set(initialState);
			},
		}),
		{
			name: 'workd:list:state',
			storage: workdStorage,
			version: LIST_STORE_PERSIST_VERSION,
			migrate: (persistedState, version) =>
				migrateListState(persistedState, version) as ListStoreState &
					ListStoreActions,
		},
	),
);
